import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye, CheckCircle, Clock, AlertCircle, Search, ChevronRight, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';

const QuoteList = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes`);
      const data = await response.json();
      setQuotes(data.quotes || []);
    } catch (err) {
      console.error('Error fetching quotes:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'sent':
        return { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Sent' };
      case 'viewed':
        return { icon: Eye, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Viewed' };
      case 'accepted':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Accepted' };
      case 'expired':
        return { icon: AlertCircle, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Expired' };
      default:
        return { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100', label: status };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesFilter = filter === 'all' || quote.status === filter;
    const siteAddress = quote.site_address || quote.site_location || '';
    const matchesSearch = searchTerm === '' || 
      quote.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      siteAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: quotes.length,
    sent: quotes.filter(q => q.status === 'sent').length,
    viewed: quotes.filter(q => q.status === 'viewed').length,
    accepted: quotes.filter(q => q.status === 'accepted').length,
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1f4e]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Quotes</h1>
          <p className="text-gray-500 text-sm mt-1">Manage and track your cleaning quotes</p>
        </div>
        <Button
          onClick={() => navigate('/admin/quotes/new')}
          className="bg-[#00c853] hover:bg-[#00a844] text-white h-11 px-5 rounded-xl font-medium"
          data-testid="create-quote-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Quote
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Quotes', value: stats.total, color: 'bg-gray-100' },
          { label: 'Sent', value: stats.sent, color: 'bg-blue-50' },
          { label: 'Viewed', value: stats.viewed, color: 'bg-amber-50' },
          { label: 'Accepted', value: stats.accepted, color: 'bg-green-50' },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.color} rounded-xl p-4`}>
            <p className="text-gray-600 text-sm">{stat.label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by company, location, or quote ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
            data-testid="quote-search-input"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'sent', 'viewed', 'accepted'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                filter === status
                  ? 'bg-[#1a1f4e] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Quotes List */}
      {filteredQuotes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No quotes found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || filter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Create your first quote to get started'}
          </p>
          {!searchTerm && filter === 'all' && (
            <Button
              onClick={() => navigate('/admin/quotes/new')}
              className="bg-[#00c853] hover:bg-[#00a844] text-white h-11 px-6 rounded-xl"
            >
              Create Quote
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4">Quote</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4 hidden md:table-cell">Site</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4 hidden lg:table-cell">Price</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4 hidden sm:table-cell">Created</th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredQuotes.map((quote) => {
                  const statusConfig = getStatusConfig(quote.status);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr 
                      key={quote.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/quotes/${quote.id}`)}
                      data-testid={`quote-row-${quote.id}`}
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{quote.company_name}</p>
                        <p className="text-sm text-gray-500">{quote.id}</p>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <p className="text-gray-700">{quote.site_address || quote.site_location}</p>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <p className="font-medium text-gray-900">{formatPrice(quote.monthly_price)}/mo</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell">
                        <p className="text-sm text-gray-500">{formatDate(quote.created_at)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteList;
